<template>
    <div>
        <rkm-area
            v-if="isSearch"
            no-margin-top
            title="Listagem de consultas agendadas"
        >

            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="handleTodosClick"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    @click="selecionarTodos()"
                    color="primary"
                    :disabled="consultas.length === 0"
                >
                    <template v-if="hasItemSelecinado">
                        Desmarcar consulta(s)
                    </template>

                    <template v-else>
                        Selecionar consultas
                    </template>
                </rkm-button>
            </template>

            <rkm-table
                :fields="consultasFields"
                :filter-ignored-fields="['dataHoraAgendamento', 'usuarioAgendamento']"
                :items="consultas"
                :tbody-tr-class="rowClass"
                class="table"
                ref="Table"
                responsive
                sort-by="horario"
            >

                <template v-slot:cell(checkbox)="{ item }">
                    <rkm-form-checkbox
                        v-model="item.selecionado"
                    />
                </template>

                <template v-slot:cell(cidadaoPacienteNome)="{index, item}">
                    <template v-if="item.cidadaoPacienteNome">
                        <rkm-button
                            :id="uniqueId(`paciente-icon-${index}`)"
                            class="mr-2"
                            small
                        >
                            <rkm-icon icon="user" />
                        </rkm-button>

                        <rkm-popover
                            :target="uniqueId(`paciente-icon-${index}`)"
                            placement="top"
                            title="Dados cadastrais"
                            triggers="hover"
                        >
                            <div>
                                <span>
                                    <strong>Data de nascimento:</strong> {{ formatDate(item.cidadaoPacienteDataNascimento) }}
                                </span>
                            </div>

                            <div>
                                <span>
                                    <strong>Idade:</strong> {{ item.cidadaoPacienteIdade }}
                                </span>
                            </div>

                            <div>
                                <span>
                                    <strong>Nome da mãe:</strong> {{item.cidadaoPacienteNomeMae}}
                                </span>
                            </div>

                            <div>
                                <span>
                                    <strong>CPF:</strong> {{item.cidadaoPacienteCpf}}
                                </span>
                            </div>

                            <div>
                                <span>
                                    <strong>CNS:</strong> {{item.cidadaoPacienteCns}}
                                </span>
                            </div>
                        </rkm-popover>

                        <template>
                            {{item.cidadaoPacienteNome}}

                            <template v-if="item.pacientePreferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>
                                    ({{item.pacienteNomeSocial}})
                                </strong>
                            </template>
                        </template>
                    </template>
                </template>

                <template v-slot:cell(statusId)="{item}">
                    <rkm-form-select
                        :label="false"
                        :options="additionals.status"
                        textField="nome"
                        v-model="item.statusId"
                        valueField="id"
                        show-default-option
                        class="pt-0 mb-0"
                        @change="handleStatus(item)"
                    />
                </template>

                <template v-slot:cell(protocoloFechamento)="{item}">
                    <rkm-form-input
                        :disabled="!item.controlaProtocoloFechamento || !item.numero || item.statusId !== enums.agendamentoStatus.REALIZADO"
                        :label="false"
                        @change="handleProtocoloFechamentoChange(item)"
                        class="pt-0 mb-0"
                        v-model="item.protocoloFechamento"
                    />
                </template>

                <template v-slot:cell(especialidadeId)="{item}">

                    <div v-if="item.especialidade">
                        {{ item.especialidade }}
                    </div>
                    <div v-else>
                        <rkm-form-select
                            :label="false"
                            :options="getEspecialidadesJornada(item)"
                            textField="nome"
                            v-model="item.especialidadeId"
                            :disabled="handleEspecidadeDisabled(item)"
                            valueField="id"
                            show-default-option
                            class="pt-0 mb-0"
                            @change="especialidadeJornadaChange(item)"
                        />
                    </div>
                </template>

                <template v-slot:cell(profissionalId)="{item}">
                    <div v-if="item.usuarioNome">
                        {{ item.usuarioNome }}
                    </div>
                    <div v-else>
                        <rkm-form-select
                            :label="false"
                            :options="item.usuariosJornada"
                            textField="nome"
                            v-model="item.usuarioId"
                            :disabled="handleUsuarioDisabled(item)"
                            valueField="id"
                            show-default-option
                            class="pt-0 mb-0"
                            @change="handleUsuario(item)"
                        />
                    </div>
                </template>

                <template v-slot:cell(procedimento)="{item}">
                    <rkm-form-input-autocomplete
                        :repository="repositories.procedimento"
                        :requestQueryStringParams="getAutocompletesRequestParams(item)"
                        :searchFields="fields.procedimento"
                        :text-field="p => `${formatProcedimento(p.codigo)} - ${p.nome}`"
                        :disabled="item.statusId == enums.agendamentoStatus.REALIZADO && item.usuarioId ? false : true"
                        name="Procedimento padrão"
                        v-rkm-tooltip.hover
                        v-model="item.procedimento"
                        @input="handleProcedimento(item)"
                    >
                        <template v-slot:itemLabel="{ item }">
                            {{ formatProcedimento(item.codigo) }} - {{ item.nome }}
                        </template>

                        <rkm-input-group-append slot="append">
                            <rkm-button
                                @click="handleModalProcedimentos(item)"
                                color="primary"
                                title="Adicionar procedimentos"
                                :disabled="item.statusId == enums.agendamentoStatus.REALIZADO && item.usuarioId ? false : true"
                            >
                                <rkm-icon icon="plus" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </template>

                <template v-slot:cell(quantidade)="{item}">
                    <rkm-form-input
                        :label="false"
                        :disabled="item.procedimento ? false : true"
                        name="Quantidade"
                        v-model="item.quantidade"
                        class="pt-0 mb-0"
                        type="number"
                        @blur="handleQuantidade(item)"
                    />
                </template>

                <template v-slot:cell(cid)="{item}">
                    <rkm-form-input-autocomplete
                        :disabled="item.statusId == enums.agendamentoStatus.REALIZADO && item.usuarioId && item.procedimento ? false : true"
                        :repository="repositories.cid"
                        :requestQueryStringParams="getCidParams(item)"
                        :searchFields="fields.cid"
                        :text-field="c => `${formatCid(c.codigo)} - ${c.nome}`"
                        name="CID"
                        v-rkm-tooltip.hover
                        v-model="item.cid"
                    >
                        <template v-slot:itemLabel="{ item }">
                            {{ formatCid(item.codigo) }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </template>

                <template v-slot:cell(actions)="{item}">

                    <rkm-button
                        @click="handleModalAtencaoBasica(item)"
                        color="primary"
                        small
                        title="Atenção básica"
                        :disabled="handleAtencaoBasicaDisabled(item)"
                    >
                        <rkm-icon icon="id-card" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <ModalAtencaoBasica
            :consultas="consultas"
            @canceled="$emit('canceled')"
            ref="ModalAtencaoBasica"
        />

        <ModalProcedimentos
            ref="ModalProcedimentos"
            @proc="handleProcedimentos"
        />
    </div>
</template>

<script>
// Components.
import ModalAtencaoBasica from './components/atencao-basica/Modal'
import ModalProcedimentos from './components/procedimentos/Modal'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import agendamentoStatus from '@/enums/ambulatorio/agendamentoStatus'

// Mixins.
import {error, file, formats, data, object, repository, stringCase} from '@/mixins'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {
        ModalAtencaoBasica,
        ModalProcedimentos
    },
    computed: {
        consultasFields() {
            return this.columns.filter(column => (column.fixed || false) || this.selectedColumns.includes(column.key))
        },
        optionalColumns() {
            return this.columns.filter(column => !(column.fixed || false))
        },
        fields() {

            return {
                procedimento: [
                    this.additionals.searchByApelido
                        ? { key: 'parametrizacao_procedimento.apelido', searchable: true }
                        : { key: 'nome', searchable: true }, { key: 'codigo', searchable: true }
                ],
                cid: [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
            }
        },
    },
    created() {
        this.repositories = {
            fechamento: this.getConectaSusRepository('ambulatorio.fechamentoConsulta'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            cid: this.getConectaSusRepository('sigtap.cid'),
        }
    },
    data: () => ({
        enums: {
            preferenciaNome,
            agendamentoStatus
        },
        selectedAll: false,
        indeterminate: false,
        selectedColumns: [],
        hasItemSelecinado: false,
        columns: [
            {key: 'checkbox', label: '#', fixed: true, class: 'col-min text-center', },
            {key: 'horario', label: 'Hora', fixed: true, class: 'col-min text-center', sortable: true},
            {key: 'cidadaoPacienteNome', label: 'Paciente', fixed: true, class: 'col-min', sortable: true},
            {key: 'cidadaoPacienteIdade', label: 'Idade', class: 'col-min', sortable: true},
            {key: 'prontuario', label: 'Prontuário', fixed: true, class: 'col-min text-center', sortable: true},
            {key: 'tipoAtendimento', label: 'Tipo de atendimento', fixed: true, class: 'col-min', sortable: true},
            {key: 'statusId', label: 'Status', fixed: true, class: 'col-status', sortable: true},
            {key: 'protocoloFechamento', label: 'Protocolo de fechamento', fixed: true, class: 'col-status', sortable: true},
            {key: 'especialidadeId', label: 'Especialidade', fixed: true, sortable: true},
            {key: 'profissionalId', label: 'Profissional', fixed: true, sortable: true},
            {key: 'procedimento', label: 'Procedimento', fixed: true, class: 'col-procedimento', sortable: true},
            {key: 'quantidade', label: 'Quantidade', fixed: true, sortable: true},
            {key: 'cid', label: 'CID', fixed: true, class: 'col-cid', sortable: true},
            {key: 'actions', label: 'Ações', fixed: true, class: 'col-min text-center'},
        ],
        usuariosJornada: [],
        saveClicked: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        handleTodosClick(selected) {
            this.selectedColumns = selected ? this.optionalColumns.map(c => c.key) : []
        },
        handleStatus(item) {

            if (item.statusId !== this.enums.agendamentoStatus.REALIZADO) {

                const index = this.consultas.findIndex(consulta => consulta.agendamentoId == item.agendamentoId
                    && consulta.atendimentoId == item.atendimentoId)

                if (index !== -1) {
                    this.consultas[index].procedimento = null;
                    this.consultas[index].cid = null;
                    this.consultas[index].procedimentos = [];
                }
            }
        },
        handleUsuario(item) {

            item.procedimento = null
        },
        handleProcedimento(item) {

            const index = this.consultas.findIndex(consulta => consulta.agendamentoId == item.agendamentoId
                && consulta.atendimentoId == item.atendimentoId)

            const procedimentoAtendimento = item.procedimentosAtendimento
                    .some(p => p.codigo == item.procedimento?.codigo)

            if(procedimentoAtendimento){

                this.showAlert('O procedimento já foi cadastrado anteriormente para este profissional nesta ficha.', "Atenção")
                item.procedimento = null

            }else if(index !== -1 &&  item.procedimento && item.procedimento.id){

                const indexProcedimentoPadrao = this.consultas[index].procedimentos
                    .findIndex(procedimento => procedimento.id === item.procedimento.id)

                if (indexProcedimentoPadrao !== -1) {

                    this.showAlert('O procedimento já foi cadastrado anteriormente para este profissional nesta ficha.', "Atenção")
                    item.procedimento = null
                }
            }

            item.quantidade = item.procedimento && item.procedimento.id ? 1 : null
            item.cid = null
        },
        selecionarTodos(){

            if(this.hasItemSelecinado == true){

                this.hasItemSelecinado = false
                this.$emit('handleSelecionado', false)
            } else {

                this.hasItemSelecinado = true
                this.$emit('handleSelecionado', true)
            }

            this.consultas.forEach(item => {

                if(this.hasItemSelecinado == true){

                    item.selecionado = true
                } else {

                    item.selecionado = false
                }
            })
        },
        especialidadeJornadaChange(item) {

            item.usuariosJornada = []

            if (item.especialidadeId) {

                const especialidade = this.especialidadesJornada
                    .find(especialidade => especialidade.id === item.especialidadeId);

                if (especialidade) {
                    item.ocupacaoId = especialidade.ocupacao.id;
                    item.criaFichaAtendimentoIndividual = especialidade.criaFichaAtendimentoIndividual
                    item.criaFichaAtendimentoOdontologico = especialidade.criaFichaAtendimentoOdontologico
                }

                const request = {
                    data: item.data,
                    unidadeId: item.unidadeId,
                    infraestruturaId: item.infraestruturaId,
                    ocupacaoId: item.ocupacaoId,
                    especialidadeId: item.especialidadeId,
                }

                this.setLoading()

                this.repositories.fechamento.getUsuariosWithJornada(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        item.usuariosJornada = response.data
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        handleModalAtencaoBasica(item) {

            const consulta = this.clone(item)

            if (!consulta.cadastro.usuario.id && consulta.usuarioId) {

                const usuario = consulta.usuariosJornada
                    .find(usuario => usuario.id === consulta.usuarioId);

                const especialidade = this.especialidadesJornada
                    .find(especialidade => especialidade.id === consulta.especialidadeId);

                if (especialidade) {
                    consulta.ocupacao = especialidade.ocupacao.codigoNome;
                    consulta.ocupacaoId = especialidade.ocupacao.id;
                }

                if (usuario) {
                    consulta.cadastro.usuario.id = usuario.id;
                    consulta.usuarioNome = usuario.nome;
                    consulta.cadastro.usuario.cidadaoNome = usuario.nome;
                    consulta.cadastro.ocupacaoId = consulta.ocupacaoId;
                }
            }

            this.$refs.ModalAtencaoBasica.open(consulta, this.form)
        },
        handleModalProcedimentos(item) {

            if (!item.cadastro.usuario.id && item.usuarioId) {

                const usuario = item.usuariosJornada.find(usuario => usuario.id === item.usuarioId);
                const especialidade = this.especialidadesJornada.find(especialidade => especialidade.id === item.especialidadeId);

                if (especialidade) {
                    item.ocupacao = especialidade.ocupacao.codigoNome;
                    item.ocupacaoId = especialidade.ocupacao.id;
                }

                if (usuario) {
                    item.cadastro.usuario.id = usuario.id;
                    item.usuarioNome = usuario.nome;
                    item.cadastro.usuario.cidadaoNome = usuario.nome;
                    item.cadastro.ocupacaoId = item.ocupacaoId;
                }
            }

            this.$refs.ModalProcedimentos.open(item)
        },
        handleEspecidadeDisabled(item) {

            return item.statusId == this.enums.agendamentoStatus.REALIZADO ? false : true
        },
        handleUsuarioDisabled(item) {

            return item.statusId == this.enums.agendamentoStatus.REALIZADO && item.especialidadeId ? false : true
        },
        saveProcedimemto(procedimento) {

            this.model.procedimentos.push(procedimento)
        },
        handleAtencaoBasicaDisabled(item) {

            return item.statusId == this.enums.agendamentoStatus.REALIZADO && item.usuarioId
                && (item.criaFichaAtendimentoIndividual || item.criaFichaAtendimentoOdontologico) ? false : true
        },
        handleQuantidade(item) {

            if (item.quantidade > item.procedimento.quantidadeMaximaExecucao) {

                this.$set(item, 'quantidade', null)

                this.showAlert(`A quantidade informada para o procedimento ${item.procedimento.nome}
                    não deve exceder o limite de ${item.procedimento.quantidadeMaximaExecucao}.`, "Atenção")
            } else if (item.quantidade === null || item.quantidade === '' || isNaN(item.quantidade)) {

                this.$set(item, 'quantidade', null)

                this.showAlert(`A quantidade informada para o procedimento ${item.procedimento.nome}
                    deve ser um número válido.`, "Atenção")
            } else if (item.quantidade <= 0) {

                this.$set(item, 'quantidade', null)

                this.showAlert(`A quantidade informada para o procedimento ${item.procedimento.nome}
                    deve ser maior que zero.`, "Atenção")
            }
        },
        getEspecialidadesJornada(item) {

            return this.especialidadesJornada.filter(especialidade =>
                (!especialidade.sexoId || especialidade.sexoId && especialidade.sexoId == item.cidadaoPacienteSexoId)
                && (!especialidade.idadeMinima || especialidade.idadeMinima && item.cidadaoPacienteIdadeAnos > especialidade.idadeMinima)
                && (!especialidade.idadeMaxima || especialidade.idadeMaxima && item.cidadaoPacienteIdadeAnos < especialidade.idadeMaxima)
            )
        },
        handleProcedimentos(procedimentos) {
            this.$emit('proc', procedimentos)
        },
        handleProtocoloFechamentoChange(item) {
            if (item.controlaProtocoloFechamento && item.numero !== item.protocoloFechamento) {
                this.showValidationNotification('O protocolo de fechamento informado é inválido.')
                item.protocoloFechamento = null
            }
        },
        getCidParams(item) {
            return {
                procedimento_id: item.procedimento && item.procedimento.hasCidPrincipal
                    ? item.procedimento.id
                    : null
            };
        },
        getAutocompletesRequestParams(item) {

            return {
                search_by_apelido: this.additionals.searchByApelido || false,
                ocupacao_id: item.ocupacaoId ?? null
            };
        },
        rowClass(item, type) {
            const showDangerRow =
                item.statusId == this.enums.agendamentoStatus.REALIZADO &&
                (
                    (item.criaFichaAtendimentoIndividual && !item.temAtendimentoIndividual) ||
                    (item.criaFichaAtendimentoOdontologico && !item.temAtendimentoOdontologico)
                )

            if(item && type === 'row' && showDangerRow && this.saveClicked) {
                return 'table-danger'
            }
        },
    },
    mixins: [error, file, formats, data, object, repository, stringCase],
    props: {
        additionals: {type: Object, required: true},
        form: {type: Object, required: true},
        especialidadesJornada: {type: Array},
        consultas: {type: Array, required: true},
        isSearch: {type: Boolean, default: false},
        selecionados: {type: Boolean, default: false},
    },
    watch: {
        selectedColumns(value) {
            this.indeterminate = value.length > 0 && value.length != this.optionalColumns.length
            this.selectedAll = value.length > 0 && value.length == this.optionalColumns.length
        },
        selecionados(value) {

            this.hasItemSelecinado = value
        },
    }
}
</script>

<style scoped>
.table >>> .col-procedimento {
    min-width: 200px;
}
.table >>> .col-status {
    min-width: 200px;
}
.table >>> .col-cid {
    min-width: 100px;
}
</style>